import { useCallback, useRef, useState } from "react";

export interface UndoableAction {
  label: string;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
}

const MAX_STACK_SIZE = 20;

/**
 * Lightweight undo/redo stack for tank planning mutations.
 * Each action stores an async undo and redo function.
 */
export function useUndoRedo() {
  const undoStack = useRef<UndoableAction[]>([]);
  const redoStack = useRef<UndoableAction[]>([]);
  const [, forceUpdate] = useState(0);

  const pushAction = useCallback((action: UndoableAction) => {
    undoStack.current = [...undoStack.current.slice(-MAX_STACK_SIZE + 1), action];
    // New action clears the redo stack
    redoStack.current = [];
    forceUpdate(n => n + 1);
  }, []);

  const undo = useCallback(async () => {
    const action = undoStack.current[undoStack.current.length - 1];
    if (!action) return;
    undoStack.current = undoStack.current.slice(0, -1);
    redoStack.current = [...redoStack.current, action];
    forceUpdate(n => n + 1);
    await action.undo();
  }, []);

  const redo = useCallback(async () => {
    const action = redoStack.current[redoStack.current.length - 1];
    if (!action) return;
    redoStack.current = redoStack.current.slice(0, -1);
    undoStack.current = [...undoStack.current, action];
    forceUpdate(n => n + 1);
    await action.redo();
  }, []);

  return {
    pushAction,
    undo,
    redo,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
    undoLabel: undoStack.current[undoStack.current.length - 1]?.label,
    redoLabel: redoStack.current[redoStack.current.length - 1]?.label,
  };
}
